import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";
import {
  rewardUserWithCash,
  rewardUserWithCoin,
  selectRewardLoading,
  selectRewardError,
  selectSuccessMessage,
  clearError,
  clearSuccessMessage,
} from "../redux/slices/rewardSlice";

const RewardAdminContent = ({
  theme,
  compact = false,
  defaultInviteCode = "",
  userName = "",
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const loading = useSelector(selectRewardLoading);
  const error = useSelector(selectRewardError);
  const successMessage = useSelector(selectSuccessMessage);

  const [rewardData, setRewardData] = useState({
    amount: "",
    inviteCode: defaultInviteCode,
    reason: "",
  });

  const [rewardType, setRewardType] = useState("cash");

  React.useEffect(() => {
    setRewardData((prev) => ({
      ...prev,
      inviteCode: defaultInviteCode || prev.inviteCode,
    }));
  }, [defaultInviteCode]);

  React.useEffect(() => {
    if (error) {
      const timer = setTimeout(() => dispatch(clearError()), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error, dispatch]);

  React.useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => dispatch(clearSuccessMessage()), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [successMessage, dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRewardData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!rewardData.amount || !rewardData.inviteCode || !rewardData.reason) {
      toast.error("Please fill in all fields");
      return;
    }

    const amount = parseFloat(rewardData.amount);
    if (Number.isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid positive amount");
      return;
    }

    try {
      if (rewardType === "cash") {
        await dispatch(
          rewardUserWithCash({
            amount,
            inviteCode: rewardData.inviteCode,
            reason: rewardData.reason,
          })
        ).unwrap();
        toast.success("User rewarded with cash successfully!");
      } else {
        await dispatch(
          rewardUserWithCoin({
            amount,
            inviteCode: rewardData.inviteCode,
            reason: rewardData.reason,
          })
        ).unwrap();
        toast.success("User rewarded with coins successfully!");
      }

      setRewardData({
        amount: "",
        inviteCode: defaultInviteCode,
        reason: "",
      });
      onSuccess?.();
    } catch (err) {
      toast.error(err.message || "Failed to reward user");
    }
  };

  const form = (
    <form onSubmit={handleSubmit} className={compact ? "au-modal-form" : "space-y-4"}>
      <div className={compact ? "au-modal-seg" : undefined}>
        {!compact ? (
          <label className="block text-sm font-medium mb-2" style={{ color: theme.text.primary }}>
            Reward Type
          </label>
        ) : (
          <span className="au-modal-field-label">Asset</span>
        )}
        <div className={compact ? "au-modal-seg-track" : "flex space-x-2"}>
          <button
            type="button"
            onClick={() => setRewardType("cash")}
            className={`${compact ? "au-modal-seg-btn" : "flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200"}${
              rewardType === "cash" ? (compact ? " au-modal-seg-btn-active" : "") : ""
            }`}
            style={
              compact
                ? undefined
                : {
                    backgroundColor: rewardType === "cash" ? theme.primary : "transparent",
                    color: rewardType === "cash" ? theme.text.primary : theme.text.tertiary,
                    borderColor: `rgba(30, 136, 229, ${theme.opacity.light})`,
                  }
            }
          >
            Cash
          </button>
          <button
            type="button"
            onClick={() => setRewardType("coin")}
            className={`${compact ? "au-modal-seg-btn" : "flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200"}${
              rewardType === "coin" ? (compact ? " au-modal-seg-btn-active" : "") : ""
            }`}
            style={
              compact
                ? undefined
                : {
                    backgroundColor: rewardType === "coin" ? theme.primary : "transparent",
                    color: rewardType === "coin" ? theme.text.primary : theme.text.tertiary,
                    borderColor: `rgba(30, 136, 229, ${theme.opacity.light})`,
                  }
            }
          >
            Coin
          </button>
        </div>
      </div>

      <label className={compact ? "au-modal-field" : "block"}>
        <span
          className={compact ? "au-modal-field-label" : "block text-sm font-medium mb-2"}
          style={compact ? undefined : { color: theme.text.primary }}
        >
          Amount
        </span>
        <input
          type="number"
          id="amount"
          name="amount"
          value={rewardData.amount}
          onChange={handleInputChange}
          placeholder={rewardType === "cash" ? "Cash amount" : "Coin amount"}
          className={compact ? "au-modal-input" : "w-full px-3 py-2 rounded-lg border text-sm"}
          style={
            compact
              ? undefined
              : {
                  backgroundColor: theme.surfaceLight,
                  border: `1px solid rgba(30, 136, 229, ${theme.opacity.light})`,
                  color: theme.text.primary,
                }
          }
          required
          min="0"
          step="0.01"
        />
      </label>

      {!defaultInviteCode ? (
        <label className={compact ? "au-modal-field" : "block"}>
          <span
            className={compact ? "au-modal-field-label" : "block text-sm font-medium mb-2"}
            style={compact ? undefined : { color: theme.text.primary }}
          >
            Invite code
          </span>
          <input
            type="text"
            name="inviteCode"
            value={rewardData.inviteCode}
            onChange={handleInputChange}
            placeholder="Member invite code"
            className={compact ? "au-modal-input au-modal-input-mono" : "w-full px-3 py-2 rounded-lg border text-sm"}
            style={
              compact
                ? undefined
                : {
                    backgroundColor: theme.surfaceLight,
                    border: `1px solid rgba(30, 136, 229, ${theme.opacity.light})`,
                    color: theme.text.primary,
                  }
            }
            required
          />
        </label>
      ) : (
        <input type="hidden" name="inviteCode" value={rewardData.inviteCode} readOnly />
      )}

      <label className={compact ? "au-modal-field" : "block"}>
        <span
          className={compact ? "au-modal-field-label" : "block text-sm font-medium mb-2"}
          style={compact ? undefined : { color: theme.text.primary }}
        >
          Reason
        </span>
        <textarea
          name="reason"
          value={rewardData.reason}
          onChange={handleInputChange}
          placeholder="Ops note for the ledger"
          rows={compact ? 2 : 3}
          className={
            compact ? "au-modal-input au-modal-textarea" : "w-full px-3 py-2 rounded-lg border text-sm resize-none"
          }
          style={
            compact
              ? undefined
              : {
                  backgroundColor: theme.surfaceLight,
                  border: `1px solid rgba(30, 136, 229, ${theme.opacity.light})`,
                  color: theme.text.primary,
                }
          }
          required
        />
      </label>

      <button
        type="submit"
        disabled={loading}
        className={
          compact
            ? "au-modal-btn au-modal-btn-reward au-modal-btn-full"
            : "w-full py-3 px-4 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        }
        style={compact ? undefined : { backgroundColor: theme.primary, color: theme.text.primary }}
      >
        {loading ? (
          <span className="au-modal-btn-loading">
            <Loader2 size={14} className="animate-spin" aria-hidden />
            Processing…
          </span>
        ) : (
          `Issue ${rewardType === "cash" ? "cash" : "coin"} reward`
        )}
      </button>
    </form>
  );

  if (compact) {
    return (
      <div className="au-reward-form">
        {error ? (
          <div className="au-modal-alert au-modal-alert-error" role="alert">
            {error}
            <button type="button" onClick={() => dispatch(clearError())} aria-label="Dismiss">
              ×
            </button>
          </div>
        ) : null}
        {successMessage ? (
          <div className="au-modal-alert au-modal-alert-success" role="status">
            {successMessage}
            <button type="button" onClick={() => dispatch(clearSuccessMessage())} aria-label="Dismiss">
              ×
            </button>
          </div>
        ) : null}
        {form}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {userName ? (
        <p className="text-sm" style={{ color: theme.text.secondary }}>
          Rewarding <strong style={{ color: theme.text.primary }}>{userName}</strong>
          {defaultInviteCode ? (
            <>
              {" "}
              · <span className="font-mono text-xs">{defaultInviteCode}</span>
            </>
          ) : null}
        </p>
      ) : null}

      {error ? (
        <div className="bg-error/10 border border-error/20 rounded-lg px-4 py-3 max-w-md mx-auto">
          <span className="text-sm font-medium" style={{ color: theme.error }}>
            {error}
          </span>
        </div>
      ) : null}

      {successMessage ? (
        <div className="bg-success/10 border border-success/20 rounded-lg px-4 py-3 max-w-md mx-auto">
          <span className="text-sm font-medium" style={{ color: theme.success }}>
            {successMessage}
          </span>
        </div>
      ) : null}

      <div className="max-w-md mx-auto">
        <div
          className="rounded-2xl p-6 border shadow-medium"
          style={{
            backgroundColor: theme.surface,
            border: `1px solid rgba(30, 136, 229, ${theme.opacity.light})`,
          }}
        >
          {form}
        </div>
      </div>
    </div>
  );
};

export default RewardAdminContent;
